import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, CalendarDays, CalendarRange, Download, Loader2, FileSignature, Home, Euro, Maximize2, Minimize2, Handshake, Shuffle } from 'lucide-react';
import { useReunionMensuelleData } from '../../../hooks/useReunionMensuelleData';
import { Granularity, getPeriodBuckets } from '../../../utils/reunionMensuelle';
import ScopeSelector, { Scope, getScopeColumns } from './ScopeSelector';
import DelaiVenteTab from './DelaiVenteTab';
import VentesTab from './VentesTab';
import HonorairesTab from './HonorairesTab';
import RetrocessionTab from './RetrocessionTab';
import InterAgenceTab from './InterAgenceTab';
import ExportReunionModal from './ExportReunionModal';

type ReunionTab = 'delai' | 'ventes' | 'honoraires' | 'retrocession' | 'interagence';

const SELECT_CLS = 'px-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors cursor-pointer';

export default function ReunionMensuelleSettings() {
  const [activeTab, setActiveTab] = useState<ReunionTab>('delai');
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const m = new Date().getMonth() + 1;
    return m < 4 ? new Date().getFullYear() - 1 : new Date().getFullYear();
  });
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [scope, setScope] = useState<Scope>({ company: 'ALL', agency: [] });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  };

  const { transactions, agencies, loading, error } = useReunionMensuelleData(fiscalYear);

  const periods = useMemo(() => getPeriodBuckets(fiscalYear, granularity), [fiscalYear, granularity]);
  const columns = useMemo(() => getScopeColumns(scope, agencies), [scope, agencies]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentFiscalYear = currentMonth < 4 ? currentYear - 1 : currentYear;
  const years = Array.from({ length: 11 }, (_, i) => currentFiscalYear - 5 + i);

  const tabs: { id: ReunionTab; label: string; icon: any }[] = [
    { id: 'delai', label: 'Délai', icon: FileSignature },
    { id: 'ventes', label: 'Vente', icon: Home },
    { id: 'honoraires', label: "Moyenne d'honoraires", icon: Euro },
    { id: 'retrocession', label: 'Rétrocession', icon: Handshake },
    { id: 'interagence', label: 'Inter-agence', icon: Shuffle },
  ];

  return (
    <div ref={containerRef} className={`space-y-5 ${isFullscreen ? 'bg-slate-50 p-6 overflow-auto h-full' : ''}`}>
      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Calendar size={15} className="text-slate-400" />
          <select value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))} className={SELECT_CLS}>
            {years.map(y => <option key={y} value={y}>Année {y} – {y + 1}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {([
            { id: 'month', label: 'Par mois', icon: CalendarDays },
            { id: 'quarter', label: 'Par trimestre', icon: CalendarRange },
            { id: 'year', label: "Par année", icon: Calendar },
          ] as { id: Granularity; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setGranularity(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                granularity === id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-slate-200 hidden sm:block" />

        <ScopeSelector scope={scope} onChange={setScope} agencies={agencies} />

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600 transition-colors"
          >
            <Download size={14} />
            Exporter
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600 transition-colors"
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          </button>
        </div>
      </div>

      {/* Sous-onglets */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-200'
                  : 'bg-white text-slate-500 border border-slate-200 hover:border-indigo-200 hover:text-indigo-600'
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="flex flex-col items-center gap-2 text-slate-400 py-16">
          <Loader2 size={22} className="animate-spin" />
          <span className="text-sm">Chargement des données…</span>
        </div>
      ) : (
        <>
          {activeTab === 'delai' && (
            <DelaiVenteTab transactions={transactions} periods={periods} columns={columns} scope={scope} />
          )}
          {activeTab === 'ventes' && (
            <VentesTab transactions={transactions} periods={periods} scope={scope} />
          )}
          {activeTab === 'honoraires' && (
            <HonorairesTab transactions={transactions} periods={periods} columns={columns} scope={scope} />
          )}
          {activeTab === 'retrocession' && (
            <RetrocessionTab transactions={transactions} periods={periods} scope={scope} />
          )}
          {activeTab === 'interagence' && (
            <InterAgenceTab transactions={transactions} agencies={agencies} periods={periods} columns={columns} scope={scope} />
          )}
        </>
      )}

      {showExportModal && (
        <ExportReunionModal
          fiscalYear={fiscalYear}
          granularity={granularity}
          scope={scope}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
